import { create } from "zustand";
import type { AnnualScheduleCellSelection, AnnualSchedulePeriod } from "../types";

interface AnnualScheduleState {
  scopeKey: string;
  selectedPeriod: AnnualSchedulePeriod;
  /** False until the user picks a period, so the package's default period applies. */
  hasSelectedPeriod: boolean;
  page: number;
  pageSize: number;
  selectedCell: AnnualScheduleCellSelection | null;

  /** Keeps preferences for the same package and resets them when its scope changes. */
  enter: (scopeKey: string) => void;

  /** Records the user's period, returning to the first page and closing cell details. */
  setPeriod: (period: AnnualSchedulePeriod) => void;

  /** Changes the page, clamping it to one or greater and closing cell details. */
  setPage: (page: number) => void;

  /** Changes league count per page and resets the page and cell selection. */
  setPageSize: (size: number) => void;

  /** Selects a cell for its details; null closes the drawer. */
  selectCell: (cell: AnnualScheduleCellSelection | null) => void;
}

type AnnualSchedulePreferences = Pick<
  AnnualScheduleState,
  "selectedPeriod" | "hasSelectedPeriod" | "page" | "pageSize" | "selectedCell"
>;

/** Initial annual-view UI preferences; query results remain in Apollo. */
export const annualScheduleDefaults: AnnualSchedulePreferences = {
  selectedPeriod: null,
  hasSelectedPeriod: false,
  page: 1,
  pageSize: 5,
  selectedCell: null,
};

/**
 * Holds annual-view preferences for the active user and package scope.
 *
 * @remarks The scope key includes user, workspace, scenario, origin, and package.
 * The application's reload on logout clears this in-memory store.
 */
export const useAnnualScheduleStore = create<AnnualScheduleState>((set, get) => ({
  scopeKey: "",
  ...annualScheduleDefaults,

  enter: scopeKey => {
    if (get().scopeKey !== scopeKey) set({ ...annualScheduleDefaults, scopeKey });
  },

  setPeriod: selectedPeriod => {
    set({ selectedPeriod, hasSelectedPeriod: true, page: 1, selectedCell: null });
  },

  setPage: page => {
    set({ page: Math.max(1, page), selectedCell: null });
  },

  setPageSize: pageSize => {
    set({ pageSize: Math.max(1, pageSize), page: 1, selectedCell: null });
  },

  selectCell: selectedCell => {
    set({ selectedCell });
  },
}));

/** Initializes annual UI preferences when the parent enters a package scope. */
export const enterAnnualScheduleScope = (scopeKey: string): void => {
  useAnnualScheduleStore.getState().enter(scopeKey);
};

/** Closes stale cell details before the parent refreshes shared package data. */
export const closeAnnualScheduleCell = (): void => {
  useAnnualScheduleStore.getState().selectCell(null);
};
